import { useReducer, useEffect, useRef, useCallback } from 'react';
import { serviceContainer } from '../../../../services/ServiceContainer';

export type RestartOverlay = 'almost-ready' | 'confirm-restart' | 'schedule' | null;

export type OverlayAction =
  | { type: 'SHOW_PROMPT' }
  | { type: 'CONFIRM' }
  | { type: 'SCHEDULE' }
  | { type: 'DISMISS' }
  | { type: 'DRIVERS_UNHEALTHY'; scheduled: boolean };

function overlayReducer(state: RestartOverlay, action: OverlayAction): RestartOverlay {
  switch (action.type) {
    case 'SHOW_PROMPT':
      return 'almost-ready';
    case 'CONFIRM':
      return 'confirm-restart';
    case 'SCHEDULE':
      return 'schedule';
    case 'DISMISS':
      return null;
    case 'DRIVERS_UNHEALTHY':
      return action.scheduled ? null : 'almost-ready';
    default:
      return state;
  }
}

function computeInitialState(driversHealthy: boolean, scheduled: boolean): RestartOverlay {
  if (!driversHealthy && !scheduled) return 'almost-ready';
  return null;
}

/**
 * Manages the restart overlay state machine with proper bidirectional
 * transition edges. When drivers transition from healthy to unhealthy,
 * the "almost-ready" prompt is shown (unless a schedule already exists).
 */
export function useRestartOverlay(
  driversHealthy: boolean,
  scheduled: boolean,
  hasIncentive = false,
): {
  overlay: RestartOverlay;
  dispatch: React.Dispatch<OverlayAction>;
  showPromptManual: () => void;
} {
  const [overlay, dispatch] = useReducer(
    overlayReducer,
    computeInitialState(driversHealthy, scheduled),
  );

  const prevHealthyRef = useRef(driversHealthy);
  const triggerRef = useRef<'auto' | 'manual'>('auto');
  const promptFiredRef = useRef(false);

  const showPromptManual = useCallback(() => {
    triggerRef.current = 'manual';
    promptFiredRef.current = false;
    dispatch({ type: 'SHOW_PROMPT' });
  }, []);

  useEffect(() => {
    if (overlay === 'almost-ready' && !promptFiredRef.current) {
      serviceContainer.analyticsManager.sendRestartPromptShownEvent({
        trigger: triggerRef.current,
        hasIncentive,
      });
      promptFiredRef.current = true;
    }
    if (overlay !== 'almost-ready') {
      promptFiredRef.current = false;
    }
  }, [overlay, hasIncentive]);

  useEffect(() => {
    const wasHealthy = prevHealthyRef.current;
    prevHealthyRef.current = driversHealthy;

    if (driversHealthy) {
      dispatch({ type: 'DISMISS' });
    } else if (wasHealthy && !driversHealthy) {
      triggerRef.current = 'auto';
      dispatch({ type: 'DRIVERS_UNHEALTHY', scheduled });
    }
  }, [driversHealthy, scheduled]);

  return { overlay, dispatch, showPromptManual };
}
